import React, { useEffect, useState, useMemo } from 'react';
import api from '../../utils/axios';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Repeat,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Loader2,
  BarChart3,
  LineChart as LineIcon,
  Layers,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const BARS_COLORS = ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#7c2d12', '#c2410c'];

const formatCurrency = (n) =>
  Number(n || 0).toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  });

export default function HistoricalTimelineCompare({ caseId, preloaded }) {
  const [granularity, setGranularity] = useState('auto');
  const [mode, setMode] = useState('both'); // 'alertas' | 'monto' | 'both'
  const [open, setOpen] = useState(true);
  const [expandCuentas, setExpandCuentas] = useState(false);
  const [expandTipos, setExpandTipos] = useState(false);
  const [data, setData] = useState(preloaded || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!caseId || !open) return;
    // Si recibimos preloaded y granularity es 'auto', úsalo directamente
    if (preloaded && granularity === 'auto') {
      setData(preloaded);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/cases/${caseId}/historical-context`, {
          params: { granularity },
        });
        if (!cancel) setData(res.data);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [caseId, granularity, open, preloaded]);

  const chartData = useMemo(() => {
    if (!data?.timeline?.periods) return [];
    return data.timeline.periods.map((p) => ({
      periodo: p.periodo,
      'Actual (alertas)': p.actual_alertas,
      'Anteriores (alertas)': p.anteriores_alertas,
      'Actual (monto)': p.actual_monto,
      'Anteriores (monto)': p.anteriores_monto,
    }));
  }, [data]);

  const comunes = data?.comunes || {};
  const casosAntCount = data?.casos_anteriores?.length || 0;

  return (
    <div className="mb-3 bg-white/80 rounded-lg border border-orange-200 overflow-hidden" data-testid="historical-timeline-compare">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-orange-100/60 transition-colors"
      >
        <div className="flex items-center gap-2 text-orange-800">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <BarChart3 size={14} />
          <span className="text-xs font-semibold uppercase tracking-wide">Comparativa visual del cliente</span>
        </div>
        <span className="text-[10px] text-orange-600">{casosAntCount} caso{casosAntCount !== 1 ? 's' : ''} anterior{casosAntCount !== 1 ? 'es' : ''}</span>
      </button>

      {open && (
        <div className="p-3 border-t border-orange-200 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-xs py-4 justify-center">
              <Loader2 size={14} className="animate-spin" />
              Cargando comparativa...
            </div>
          )}

          {!loading && data && (
            <>
              {/* Control bar */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide text-slate-500">Ver:</span>
                <div className="inline-flex rounded-md overflow-hidden border border-orange-200 bg-white" role="group">
                  {[
                    { k: 'alertas', label: 'Alertas', Icon: BarChart3 },
                    { k: 'monto', label: 'Monto', Icon: DollarSign },
                    { k: 'both', label: 'Ambos', Icon: Layers },
                  ].map(({ k, label, Icon }) => (
                    <button
                      key={k}
                      onClick={() => setMode(k)}
                      className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        mode === k ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-orange-100'
                      }`}
                      data-testid={`mode-${k}`}
                    >
                      <Icon size={11} />
                      {label}
                    </button>
                  ))}
                </div>

                <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500">Granularidad:</span>
                <div className="inline-flex rounded-md overflow-hidden border border-orange-200 bg-white">
                  {[
                    { k: 'auto', label: 'Auto' },
                    { k: 'month', label: 'Mensual' },
                    { k: 'week', label: 'Semanal' },
                  ].map(({ k, label }) => (
                    <button
                      key={k}
                      onClick={() => setGranularity(k)}
                      className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        granularity === k ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-orange-100'
                      }`}
                      data-testid={`granularity-${k}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <span className="ml-auto text-[10px] text-slate-500">
                  {chartData.length} período{chartData.length !== 1 ? 's' : ''} ·{' '}
                  {data.timeline?.granularity === 'month' ? 'mensual' : 'semanal'}
                </span>
              </div>

              {/* Chart */}
              {chartData.length > 0 ? (
                <div style={{ width: '100%', height: 220 }} data-testid="timeline-chart">
                  <ResponsiveContainer>
                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#fde68a" />
                      <XAxis dataKey="periodo" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Alertas', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#92400e' } }} />
                      {(mode === 'monto' || mode === 'both') && (
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                          label={{ value: 'Monto', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#92400e' } }}
                        />
                      )}
                      <RechartsTooltip
                        formatter={(value, name) => {
                          if (name.includes('monto')) return [formatCurrency(value), name];
                          return [value, name];
                        }}
                        contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #fed7aa' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {(mode === 'alertas' || mode === 'both') && (
                        <>
                          <Bar yAxisId="left" dataKey="Actual (alertas)" fill={BARS_COLORS[0]} />
                          <Bar yAxisId="left" dataKey="Anteriores (alertas)" fill={BARS_COLORS[4]} />
                        </>
                      )}
                      {(mode === 'monto' || mode === 'both') && (
                        <>
                          <Line yAxisId="right" type="monotone" dataKey="Actual (monto)" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                          <Line yAxisId="right" type="monotone" dataKey="Anteriores (monto)" stroke="#7c2d12" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                        </>
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center text-xs text-slate-500 py-4">
                  No hay datos con fecha para graficar en el período disponible.
                </div>
              )}

              {/* Chips de coincidencias */}
              <div className="flex flex-wrap gap-2 pt-1">
                {comunes.cuentas_comunes?.length > 0 && (
                  <button
                    onClick={() => setExpandCuentas((v) => !v)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-medium hover:bg-amber-200 transition-colors"
                    data-testid="chip-cuentas-comunes"
                  >
                    <Users size={11} />
                    {comunes.cuentas_comunes.length} cuenta{comunes.cuentas_comunes.length !== 1 ? 's' : ''} en común
                    {expandCuentas ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  </button>
                )}
                {comunes.tipos_comunes?.length > 0 && (
                  <button
                    onClick={() => setExpandTipos((v) => !v)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-300 text-[11px] font-medium hover:bg-rose-200 transition-colors"
                    data-testid="chip-tipos-comunes"
                  >
                    <Repeat size={11} />
                    {comunes.tipos_comunes.length} tipo{comunes.tipos_comunes.length !== 1 ? 's' : ''} de alerta repetido{comunes.tipos_comunes.length !== 1 ? 's' : ''}
                    {expandTipos ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  </button>
                )}
                {typeof comunes.monto_variacion_pct === 'number' && (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border ${
                      comunes.monto_variacion_pct >= 0
                        ? 'bg-red-100 text-red-800 border-red-300'
                        : 'bg-green-100 text-green-800 border-green-300'
                    }`}
                    title={`Promedio anterior: ${formatCurrency(comunes.monto_promedio_anterior)} vs actual: ${formatCurrency(comunes.monto_actual)}`}
                    data-testid="chip-monto-variacion"
                  >
                    {comunes.monto_variacion_pct >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                    Monto {comunes.monto_variacion_pct >= 0 ? 'aumentó' : 'bajó'} {Math.abs(comunes.monto_variacion_pct)}% vs promedio anterior
                  </span>
                )}
              </div>

              {/* Expandible: cuentas en común */}
              {expandCuentas && comunes.cuentas_comunes?.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px]">
                  <div className="font-semibold text-amber-800 mb-1">Cuentas que aparecen en múltiples casos</div>
                  <div className="flex flex-wrap gap-1">
                    {comunes.cuentas_comunes.map((c, i) => (
                      <span key={i} className="bg-white rounded px-2 py-0.5 border border-amber-200 font-mono text-slate-700">
                        {c.cuenta} <span className="text-amber-600">({c.casos}×)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {expandTipos && comunes.tipos_comunes?.length > 0 && (
                <div className="rounded border border-rose-200 bg-rose-50 p-2 text-[11px]">
                  <div className="font-semibold text-rose-800 mb-1">Tipos de alerta repetidos</div>
                  <div className="flex flex-wrap gap-1">
                    {comunes.tipos_comunes.map((t, i) => (
                      <span key={i} className="bg-white rounded px-2 py-0.5 border border-rose-200 text-slate-700">
                        {t.tipo} <span className="text-rose-600">({t.casos}×)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
